// GeocoderBar.jsx
// Search bar that geocodes place names using Nominatim (no API key required)
// and flies the map to the result.

import React, {useState, useRef} from 'react';
import {fromLonLat} from 'ol/proj';
import {Box, InputBase, IconButton, Tooltip, Typography} from '@mui/material';
import SearchRounded from '@mui/icons-material/SearchRounded';

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';

const GeocoderBar = ({mapRef}) => {
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const abortRef = useRef(null);

    const handleSearch = async (e) => {
        e.preventDefault();
        const q = query.trim();
        if (!q) return;

        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setLoading(true);
        setError(null);

        try {
            const url = `${NOMINATIM_URL}?q=${encodeURIComponent(q)}&format=json&limit=1`;
            const res = await fetch(url, {
                signal: controller.signal,
                headers: {'Accept-Language': 'en'},
            });
            if (!res.ok) throw new Error('Geocoder request failed');
            const data = await res.json();
            if (!data || data.length === 0) {
                setError('No results found');
                return;
            }
            const {lat, lon} = data[0];
            const map = mapRef?.current?.getMap?.() ?? mapRef?.current;
            if (map && map.getView) {
                const center = fromLonLat([parseFloat(lon), parseFloat(lat)]);
                map.getView().animate({center, zoom: 12, duration: 1200});
            }
        } catch (err) {
            if (err?.name !== 'AbortError') {
                setError('Search failed');
                console.warn('[GeocoderBar]', err);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            component="form"
            onSubmit={handleSearch}
            role="search"
            aria-label="Geocode location"
            sx={{display: 'flex', alignItems: 'center', gap: 0.5, position: 'relative'}}
        >
            <Box
                sx={(theme) => ({
                    display: 'flex',
                    alignItems: 'center',
                    height: 30,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    px: 1,
                    bgcolor: 'background.default',
                    '&:focus-within': {borderColor: 'primary.main'},
                })}
            >
                <SearchRounded sx={{fontSize: 16, color: 'text.disabled', mr: 0.5}}/>
                <InputBase
                    type="search"
                    placeholder="Search location…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    inputProps={{'aria-label': 'Location search'}}
                    sx={{fontSize: 13, width: 200, '& input': {p: 0}}}
                />
            </Box>
            <Tooltip title="Search">
                <span>
                    <IconButton
                        type="submit"
                        size="small"
                        disabled={loading || !query.trim()}
                        aria-label="Search"
                        sx={{
                            borderRadius: 1,
                            px: 1,
                            fontSize: 13,
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        {loading ? '…' : 'Go'}
                    </IconButton>
                </span>
            </Tooltip>
            {error && (
                <Typography
                    role="alert"
                    variant="caption"
                    sx={{
                        position: 'absolute',
                        top: '110%',
                        left: 0,
                        color: 'error.main',
                        bgcolor: 'background.paper',
                        px: 1,
                        py: 0.25,
                        borderRadius: 0.5,
                        border: '1px solid',
                        borderColor: 'error.light',
                        whiteSpace: 'nowrap',
                        zIndex: 10,
                    }}
                >
                    {error}
                </Typography>
            )}
        </Box>
    );
};

export default GeocoderBar;
